// Copyright (c) StrangeBee 2024

import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { slugify, useLocalMessage } from '@Helpers/';
import { FormInstance } from 'antd/lib/form';
import { FormSubmitButton, FormSubmitOptions } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import { THForm } from '@Molecules/THForm/THForm';
import { THInput } from '@Molecules/Inputs/THInput/THInput';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THNumberSelect } from '@Molecules/Inputs/THNumberSelect/THNumberSelect';
import { ICONS } from '@Constants/iconsApp';
import { copyToClipboard } from '@Helpers/copy';
import client from '@Client';
import { Store } from 'antd/lib/form/interface';
import { useWatch } from 'antd/lib/form/Form';

import AceEditor from 'react-ace';

import 'ace-builds/src-noconflict/theme-tomorrow';
import 'ace-builds/src-noconflict/mode-jsx';
import 'ace-builds/src-noconflict/mode-xml';
import 'ace-builds/src-noconflict/snippets/xml';

import './SAMLForm.scss';

export interface SAMLInput extends FormSubmitOptions {
    type: string;
    displayName?: string;
    name?: string;
    idpMetadata?: string;
    idpMetadataUrl?: string;
    maximumAuthenticationLifetime?: string;

    metadataType?: string;
    callbackURL?: string;
}

export interface SAMLValueFormOutput extends SAMLInput {
    another?: boolean;
}

interface SAMLFormProps {
    form: FormInstance;
    defaultValues?: SAMLInput;
    isValid: boolean;
    onFinish: (values: SAMLValueFormOutput) => void;
    onChange: () => void;
    onCancel?: () => void;
    forbiddenNames?: string[];
}

export const SAMLForm = ({
    form,
    defaultValues,
    isValid,
    forbiddenNames,
    onFinish,
    onChange,
    onCancel,
}: SAMLFormProps): ReactElement => {
    const i = useLocalMessage();

    const defaultType = useMemo(
        () => (defaultValues?.idpMetadataUrl ? 'idpMetadataUrl' : defaultValues?.idpMetadata ? 'idpMetadata' : ''),
        [defaultValues],
    );
    const [formType, setFormType] = useState<string>(defaultType);
    const [toggleCallbackURL, setToggleCallbackURL] = useState(!!defaultValues?.displayName);

    const callbackURL = useWatch(['callbackURL'], form);

    const baseUrl = window.location.origin + client.apiUrl;

    const onValuesChange = (changedValue: Store): void => {
        if (changedValue.hasOwnProperty('displayName')) {
            form.setFieldsValue({
                callbackURL: `${baseUrl}/sso/${changedValue?.displayName && slugify(changedValue.displayName)}`,
            });
            setToggleCallbackURL(true);
        }

        onChange();
    };

    const onFinishHandler = useCallback(
        (values: SAMLInput) => {
            if (values.metadataType === 'idpMetadataUrl') {
                delete values.idpMetadata;
            }

            if (values.metadataType === 'idpMetadata') {
                delete values.idpMetadataUrl;
            }
            delete values.metadataType;

            if (values.displayName && values.displayName.trim() !== '') {
                values.name = slugify(values.displayName);
            }
            delete values.callbackURL;

            onFinish(values);
        },
        [onFinish],
    );

    return (
        <THForm<SAMLInput>
            name="task-form"
            className="saml-form"
            onFinish={onFinishHandler}
            initialValues={{
                ...defaultValues,
                type: 'saml',
                callbackURL: defaultValues?.displayName && `${baseUrl}/sso/${slugify(defaultValues.displayName)}`,
                maximumAuthenticationLifetime: defaultValues?.maximumAuthenticationLifetime || '1 hours',
                metadataType: defaultType,
            }}
            form={form}
            onValuesChange={onValuesChange}
        >
            <THForm.Layout footer={<FormSubmitButton onCancel={onCancel} disabled={!isValid} formName="task-form" />}>
                <THForm.Item name="type" label={i('configPlateform.saml.type')}>
                    <THInput disabled={true} />
                </THForm.Item>

                <THForm.Item
                    name="displayName"
                    label={i('configPlateform.saml.name')}
                    rules={[
                        { required: !defaultValues?.displayName, message: i('configPlateform.required.general') },
                        {
                            validator: (_, value) => {
                                const nameTreated = slugify(value);

                                if (defaultValues?.displayName === value) {
                                    return Promise.resolve(i('common.already-exist'));
                                }

                                if (forbiddenNames?.includes(nameTreated)) {
                                    return Promise.reject(i('common.already-exist'));
                                }

                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <THInput disabled={!!defaultValues?.displayName} />
                </THForm.Item>

                <THForm.Item
                    name="metadataType"
                    rules={[{ required: true }]}
                    label={i('configPlateform.saml.idpMetadata-type')}
                >
                    <THSelect
                        placeholder="Choose type"
                        onChange={(value: string) => setFormType(value)}
                        options={[
                            {
                                label: 'xml',
                                value: 'idpMetadata',
                            },
                            { label: 'url', value: 'idpMetadataUrl' },
                        ]}
                    />
                </THForm.Item>

                <THForm.Item
                    name="idpMetadata"
                    label={i('configPlateform.saml.idpMetadata-value')}
                    rules={[{ required: formType === 'idpMetadata' }]}
                    hidden={formType !== 'idpMetadata'}
                >
                    <AceEditor
                        width="100%"
                        height="15rem"
                        mode={'xml'}
                        theme="tomorrow"
                        setOptions={{ useWorker: false }}
                    />
                </THForm.Item>

                <THForm.Item
                    name="idpMetadataUrl"
                    label={i('configPlateform.saml.idpMetadata-value')}
                    rules={[{ required: formType === 'idpMetadataUrl', type: 'url' }]}
                    hidden={formType !== 'idpMetadataUrl'}
                >
                    <THInput />
                </THForm.Item>

                <THForm.Item name="mapperIDPLoginKey" label={i('configPlateform.saml.idp-login-key')}>
                    <THInput placeholder={i('configPlateform.saml.placeholders.idp-login-key')} />
                </THForm.Item>

                <THForm.Item
                    name="maximumAuthenticationLifetime"
                    label={i('configPlateform.saml.maximumAuthenticationLifetime')}
                >
                    <THNumberSelect options={['minutes', 'hours', 'days']} min={1} />
                </THForm.Item>

                <THForm.Item
                    name="callbackURL"
                    label={i('configPlateform.saml.callbackURL')}
                    hidden={!toggleCallbackURL}
                >
                    <THInput
                        className="saml-form__callback-url"
                        disabled
                        addonAfter={
                            <ICONS.CloneIcon
                                role="button"
                                onClick={() => {
                                    copyToClipboard(callbackURL, i('notification.copy'));
                                }}
                            />
                        }
                    />
                </THForm.Item>
            </THForm.Layout>
        </THForm>
    );
};
